import {
  type StepBuildSkeleton,
  type BuildEditMode
} from './StepBuildSkeleton'
import {
  validate_bones,
  validate_skeleton_name
} from '../../../features/skeleton/SkeletonValidator'
import { CustomSkeletonStorage } from '../../../features/persistence/CustomSkeletonStorage'
import {
  BONE_DISPLAY_SHAPES,
  type BoneDisplayShape,
  type SkeletonDescriptor
} from '../../../features/skeleton/SkeletonDescriptor'
import { confirm_action } from '../../ConfirmationDialog.ts'

/**
 * BuildSkeletonPanel
 *
 * DOM-side renderer for the Build Skeleton authoring step. Subscribes to a
 * `StepBuildSkeleton` and re-renders the bone list, button enable states, and
 * inline validation messages whenever the editor state changes.
 *
 * The panel host (a single root element with id `#build-skeleton-tools`) is
 * shared between the inline (create.html) and standalone (build-skeleton.html)
 * entry points — same DOM, same panel.
 *
 * Save flow: prompts the user for a skeleton name (or uses the current one),
 * validates the bone tree via `SkeletonValidator`, persists via
 * `CustomSkeletonStorage.put`, then invokes the `on_save` callback so the host
 * page can navigate (back to LoadSkeleton in the inline case, back to the
 * library list in the standalone case).
 */

export interface BuildSkeletonPanelOptions {
  step: StepBuildSkeleton
  root: HTMLElement
  on_save: (descriptor: SkeletonDescriptor) => void
  on_cancel: () => void
  /** Optional renderer-canvas snapshot used to attach a preview thumbnail to
   *  the saved descriptor. Returns undefined when capture isn't available
   *  (e.g., page just loaded with no model). */
  capture_thumbnail?: (width: number, height: number) => string | undefined
}

export class BuildSkeletonPanel {
  private readonly step: StepBuildSkeleton
  private readonly root: HTMLElement
  private readonly on_save: (descriptor: SkeletonDescriptor) => void
  private readonly on_cancel: () => void
  private readonly capture_thumbnail: ((width: number, height: number) => string | undefined) | undefined
  private unsubscribe: (() => void) | null = null

  constructor (options: BuildSkeletonPanelOptions) {
    this.step = options.step
    this.root = options.root
    this.on_save = options.on_save
    this.on_cancel = options.on_cancel
    this.capture_thumbnail = options.capture_thumbnail
  }

  public mount (): void {
    this.root.innerHTML = this.template()
    this.bind_static_handlers()
    this.unsubscribe = this.step.on_state_change(() => this.render())
    this.render()
  }

  public unmount (): void {
    if (this.unsubscribe !== null) {
      this.unsubscribe()
      this.unsubscribe = null
    }
    this.root.innerHTML = ''
  }

  // --- rendering ----------------------------------------------------------

  private template (): string {
    return `
      <div class="build-skeleton-panel" style="display: flex; flex-direction: column; gap: 0.75rem;">
        <header style="display: flex; flex-direction: column; gap: 0.25rem;">
          <span style="font-weight: 600;">Build Skeleton</span>
          <input type="text" id="build-skeleton-name-input" maxlength="64" placeholder="Skeleton name" />
        </header>

        <div style="display: flex; gap: 0.5rem; flex-wrap: wrap;">
          <button id="build-add-root-btn" class="secondary-button">+ Root Bone</button>
          <button id="build-add-child-btn" class="secondary-button" disabled>+ Child Bone</button>
          <button id="build-add-connected-child-btn" class="secondary-button" disabled title="Add a child bone whose head sits at the selected bone's tail. Makes connected chains (no click-to-place needed).">+ Connected Child</button>
        </div>

        <div style="display: flex; gap: 0.5rem; flex-wrap: wrap; align-items: center;">
          <input type="file" id="build-load-reference-input" accept=".glb,.gltf,model/gltf-binary,model/gltf+json" style="display: none;" />
          <button id="build-load-reference-btn" class="secondary-button" title="Load a GLB to use as a reference. If it has a skeleton, those bones populate the editor and you can add or remove bones from there.">Load reference GLB</button>
          <button id="build-clear-reference-btn" class="no-style-button" disabled title="Remove the loaded reference GLB from the viewport. Bones imported from it stay until you undo.">Clear reference</button>
        </div>
        <div id="build-reference-status" style="font-size: 0.85em; opacity: 0.8; min-height: 1.2em;"></div>

        <div id="build-add-mode-hint" style="font-size: 0.85em; opacity: 0.8; min-height: 1.2em;"></div>

        <fieldset style="display: flex; flex-direction: column; gap: 0.25rem;">
          <legend style="font-size: 0.85em;">Display</legend>
          <label style="display: flex; flex-direction: row; align-items: center; gap: 0.5rem;">
            <span style="min-width: 70px;">Thickness</span>
            <input type="range" id="build-thickness-slider" min="0.5" max="6" step="0.1" value="1.0" style="flex: 1;" />
            <span id="build-thickness-readout" style="min-width: 30px; text-align: right; font-variant-numeric: tabular-nums;">1.0&times;</span>
          </label>
          <label style="display: flex; gap: 0.5rem; align-items: center;" title="Show a small RGB axis triad on each bone. Useful for visualizing twist axes when setting joint limits.">
            <input type="checkbox" id="build-axis-triads-toggle" />
            <span>Show bone axis triads</span>
          </label>
          <label style="display: flex; gap: 0.5rem; align-items: center;" title="Tint the reference mesh's vertices by their nearest bone. Quick visual sanity check that every region of the mesh has a bone near it. Not a real skinning preview.">
            <input type="checkbox" id="build-influence-preview-toggle" />
            <span>Show influence preview</span>
          </label>
        </fieldset>

        <fieldset style="display: flex; flex-direction: column; gap: 0.25rem;">
          <legend style="font-size: 0.85em;">Placement</legend>
          <label style="display: flex; gap: 0.5rem; align-items: center;" title="When placing or moving a bone, snap to the nearest mesh vertex instead of the exact surface point.">
            <input type="checkbox" id="build-snap-vertex-toggle" />
            <span>Snap placement to nearest vertex</span>
          </label>
          <label style="display: flex; gap: 0.5rem; align-items: center;" title="When you finish moving/rotating a bone with an _l/_r side suffix, automatically mirror the change onto its counterpart bone.">
            <input type="checkbox" id="build-live-mirror-toggle" />
            <span>Live mirror (sync _l/_r counterparts)</span>
          </label>
        </fieldset>

        <fieldset style="display: flex; flex-direction: column; gap: 0.35rem;">
          <legend style="font-size: 0.85em;">Cleanup tools</legend>
          <div style="display: flex; gap: 4px; align-items: center;" title="Apply a find/replace to every bone name. Pick a preset or type your own. Bones whose new name would collide with another bone are skipped.">
            <select id="build-rename-preset" style="flex: 1;">
              <option value="custom">Custom find/replace</option>
              <option value="strip-mixamo">Strip "mixamorig:" prefix</option>
              <option value="lower">Lowercase all bone names</option>
              <option value="dot-to-underscore">Replace "." with "_"</option>
            </select>
          </div>
          <div style="display: flex; gap: 4px; align-items: center;">
            <input type="text" id="build-rename-find" placeholder="find" style="flex: 1; min-width: 0;" />
            <span>→</span>
            <input type="text" id="build-rename-replace" placeholder="replace" style="flex: 1; min-width: 0;" />
            <button id="build-rename-apply-btn" class="secondary-button">Apply</button>
          </div>
          <label style="display: flex; gap: 4px; align-items: center;" title="Delete bones whose length (tail offset in their own local frame) is below this threshold. Children reparent to the deleted bone's parent so chains stay connected.">
            <span style="min-width: 110px;">Prune bones &lt;</span>
            <input type="number" id="build-prune-threshold" min="0" step="0.005" value="0.02" style="width: 70px;" />
            <button id="build-prune-apply-btn" class="secondary-button">Prune</button>
          </label>
          <div style="display: flex; gap: 4px; align-items: center;" title="Detect bone pairs whose world positions are mirror-X of each other and rename them with _l/_r suffixes. Makes the existing Mirror tools work on imported skeletons.">
            <button id="build-auto-symmetrize-btn" class="secondary-button">Auto-name _l/_r pairs by symmetry</button>
          </div>
          <div id="build-tools-status" style="font-size: 0.85em; opacity: 0.85; min-height: 1.2em;"></div>
        </fieldset>

        <fieldset style="display: flex; flex-direction: column; gap: 0.25rem;">
          <legend style="font-size: 0.85em;">Bones</legend>
          <ul id="build-bone-list" style="list-style: none; padding: 0; margin: 0; max-height: 220px; overflow-y: auto;"></ul>
        </fieldset>

        <fieldset id="build-selected-bone-editor" style="display: flex; flex-direction: column; gap: 0.5rem;">
          <legend style="font-size: 0.85em;">Selected bone</legend>

          <label style="display: flex; flex-direction: column; gap: 0.25rem;">
            <span>Name</span>
            <input type="text" id="build-selected-name-input" maxlength="64" disabled />
          </label>

          <label style="display: flex; flex-direction: column; gap: 0.25rem;" title="Reparent this bone. Choices that would create a parent cycle are filtered out automatically.">
            <span>Parent</span>
            <select id="build-selected-parent-select" disabled></select>
          </label>

          <div style="display: flex; flex-direction: column; gap: 0.25rem;">
            <span style="font-size: 0.85em;">Edit mode</span>
            <div style="display: flex; gap: 4px;">
              <button class="secondary-button" data-edit-mode="translate_head" disabled>Move</button>
              <button class="secondary-button" data-edit-mode="rotate" disabled>Rotate</button>
              <button class="secondary-button" data-edit-mode="translate_tail" disabled>Tail</button>
            </div>
          </div>

          <div style="display: flex; gap: 0.5rem;">
            <button id="build-mirror-bone-btn" class="secondary-button" disabled title="Mirror this bone and its descendants across the X axis (creates _l/_r counterparts).">Mirror to other side</button>
            <button id="build-delete-bone-btn" class="secondary-button" disabled title="Delete this bone; children stay and reparent to its parent.">Delete bone</button>
            <button id="build-delete-subtree-btn" class="secondary-button" disabled title="Delete this bone and all descendants. Asks for confirmation when descendants exist.">Delete subtree</button>
          </div>

          <label style="display: flex; flex-direction: row; align-items: center; gap: 0.5rem;" title="Mark this bone as an IK target. When greater than 0, dragging this bone in Move mode bends the previous N parent bones to follow it (CCD solver). 0 = normal forward-kinematic bone.">
            <span style="min-width: 100px;">IK chain length</span>
            <input type="number" id="build-ik-chain-input" min="0" max="16" step="1" value="0" disabled style="width: 60px;" />
          </label>

          <label style="display: flex; flex-direction: row; align-items: center; gap: 0.5rem;" title="Visual shape for this bone's head handle. Cosmetic only — useful for marking control bones with a distinctive shape.">
            <span style="min-width: 100px;">Display shape</span>
            <select id="build-display-shape" disabled>
              ${BONE_DISPLAY_SHAPES.map(s => `<option value="${s}">${s.charAt(0).toUpperCase() + s.slice(1)}</option>`).join('')}
            </select>
          </label>

          <fieldset id="build-pole-fieldset" style="display: flex; flex-direction: column; gap: 0.25rem;">
            <legend style="font-size: 0.85em;" title="World-space direction the IK chain should bend toward. Resolves the elbow/knee orientation. Only used on IK targets (chain length > 0).">Pole vector</legend>
            <div style="display: flex; flex-direction: row; gap: 4px; align-items: center;">
              <span style="width: 12px;">X</span><input type="number" id="build-pole-x" step="0.1" value="0" disabled style="width: 50px;" />
              <span style="width: 12px;">Y</span><input type="number" id="build-pole-y" step="0.1" value="0" disabled style="width: 50px;" />
              <span style="width: 12px;">Z</span><input type="number" id="build-pole-z" step="0.1" value="0" disabled style="width: 50px;" />
              <button id="build-pole-clear" class="no-style-button" disabled style="margin-left: auto;" title="Clear pole vector">✕</button>
            </div>
          </fieldset>

          <fieldset id="build-limits-fieldset" style="display: flex; flex-direction: column; gap: 0.35rem;">
            <legend style="font-size: 0.85em;" title="Swing-twist rotation limits. The bone's rotation is split into a twist around the chosen axis and a swing perpendicular to it. Avoids gimbal-lock issues that per-axis Euler limits suffer.">Joint limits</legend>
            <label style="display: flex; gap: 0.5rem; align-items: center;">
              <input type="checkbox" id="build-limits-enable" disabled />
              <span>Enable</span>
            </label>
            <label style="display: flex; gap: 0.5rem; align-items: center;" title="Axis of the bone's local frame around which 'twist' is measured. Other rotations are 'swing'. Y is the typical default for limb bones.">
              <span style="min-width: 80px;">Twist axis</span>
              <select id="build-limit-twist-axis" disabled>
                <option value="x">X</option>
                <option value="y" selected>Y</option>
                <option value="z">Z</option>
              </select>
            </label>
            <label style="display: flex; gap: 4px; align-items: center;" title="Twist range in degrees. For a hinge like an elbow, set swing max to 0 and the twist range to the bend range.">
              <span style="min-width: 80px;">Twist range</span>
              <input type="number" id="build-limit-twist-min" step="5" value="-180" disabled style="width: 60px;" />
              <span>to</span>
              <input type="number" id="build-limit-twist-max" step="5" value="180" disabled style="width: 60px;" />
            </label>
            <label style="display: flex; gap: 0.5rem; align-items: center;" title="Swing cone half-angle in degrees. The bone may deviate from its rest direction by at most this much. Set to 0 to lock swing entirely (hinge joint).">
              <span style="min-width: 80px;">Swing cone</span>
              <input type="number" id="build-limit-swing-max" min="0" max="180" step="5" value="180" disabled style="width: 60px;" />
              <span>deg</span>
            </label>
          </fieldset>
        </fieldset>

        <div style="display: flex; gap: 0.5rem;">
          <button id="build-undo-btn" class="secondary-button" disabled>Undo</button>
          <button id="build-redo-btn" class="secondary-button" disabled>Redo</button>
        </div>

        <div id="build-validation-message" style="font-size: 0.85em; color: #d4453a; min-height: 1.2em;"></div>

        <div style="display: flex; gap: 0.5rem;">
          <button id="build-cancel-btn" class="no-style-button">Cancel</button>
          <button id="build-save-btn">Save Skeleton</button>
        </div>
      </div>
    `
  }

  private bind_static_handlers (): void {
    this.q<HTMLInputElement>('#build-skeleton-name-input').addEventListener('input', (e) => {
      this.step.set_skeleton_name((e.target as HTMLInputElement).value)
    })

    const thickness_input = this.q<HTMLInputElement>('#build-thickness-slider')
    const thickness_readout = this.q<HTMLSpanElement>('#build-thickness-readout')
    thickness_input.value = this.step.get_bone_display_thickness().toString()
    thickness_readout.textContent = `${this.step.get_bone_display_thickness().toFixed(1)}×`
    thickness_input.addEventListener('input', () => {
      const value = parseFloat(thickness_input.value)
      if (!Number.isFinite(value)) return
      this.step.set_bone_display_thickness(value)
      thickness_readout.textContent = `${value.toFixed(1)}×`
    })

    this.q<HTMLButtonElement>('#build-add-root-btn').addEventListener('click', () => {
      this.step.set_add_bone_mode('place_root')
    })
    this.q<HTMLButtonElement>('#build-add-child-btn').addEventListener('click', () => {
      this.step.set_add_bone_mode('place_child')
    })
    this.q<HTMLButtonElement>('#build-add-connected-child-btn').addEventListener('click', () => {
      this.step.add_connected_child_bone()
    })

    this.q<HTMLInputElement>('#build-axis-triads-toggle').addEventListener('change', (e) => {
      this.step.set_axis_triads_visible((e.target as HTMLInputElement).checked)
    })
    this.q<HTMLInputElement>('#build-influence-preview-toggle').addEventListener('change', (e) => {
      this.step.set_influence_preview_enabled((e.target as HTMLInputElement).checked)
    })
    this.q<HTMLInputElement>('#build-snap-vertex-toggle').addEventListener('change', (e) => {
      this.step.set_snap_to_vertex_enabled((e.target as HTMLInputElement).checked)
    })
    this.q<HTMLInputElement>('#build-live-mirror-toggle').addEventListener('change', (e) => {
      this.step.set_live_mirror_enabled((e.target as HTMLInputElement).checked)
    })

    const rename_preset = this.q<HTMLSelectElement>('#build-rename-preset')
    const rename_find = this.q<HTMLInputElement>('#build-rename-find')
    const rename_replace = this.q<HTMLInputElement>('#build-rename-replace')
    rename_preset.addEventListener('change', () => {
      const preset = rename_preset.value
      if (preset === 'strip-mixamo') {
        rename_find.value = 'mixamorig:'
        rename_replace.value = ''
      } else if (preset === 'lower') {
        rename_find.value = ''
        rename_replace.value = ''
      } else if (preset === 'dot-to-underscore') {
        rename_find.value = '.'
        rename_replace.value = '_'
      }
    })
    this.q<HTMLButtonElement>('#build-rename-apply-btn').addEventListener('click', () => {
      const preset = rename_preset.value
      try {
        let renamed = 0
        if (preset === 'lower') {
          renamed = this.step.transform_bone_names((name) => name.toLowerCase())
        } else {
          renamed = this.step.apply_name_mapping(rename_find.value, rename_replace.value)
        }
        this.set_tools_status(renamed === 0
          ? 'No bones renamed.'
          : `Renamed ${renamed} bone(s).`)
      } catch (err) {
        this.set_tools_status((err as Error).message)
      }
    })

    this.q<HTMLButtonElement>('#build-prune-apply-btn').addEventListener('click', () => {
      const threshold_input = this.q<HTMLInputElement>('#build-prune-threshold')
      const threshold = parseFloat(threshold_input.value)
      if (!Number.isFinite(threshold) || threshold <= 0) {
        this.set_tools_status('Prune threshold must be a positive number.')
        return
      }
      try {
        const removed = this.step.prune_small_bones(threshold)
        this.set_tools_status(removed === 0
          ? 'No bones smaller than the threshold.'
          : `Pruned ${removed} bone(s).`)
      } catch (err) {
        this.set_tools_status((err as Error).message)
      }
    })

    this.q<HTMLButtonElement>('#build-auto-symmetrize-btn').addEventListener('click', () => {
      try {
        const renamed = this.step.auto_symmetrize_names()
        this.set_tools_status(renamed === 0
          ? 'No mirrored bone pairs detected.'
          : `Auto-named ${renamed} bone(s) into _l/_r pairs.`)
      } catch (err) {
        this.set_tools_status((err as Error).message)
      }
    })

    this.q<HTMLSelectElement>('#build-selected-parent-select').addEventListener('change', (e) => {
      const value = (e.target as HTMLSelectElement).value
      const new_parent = value === '' ? null : parseInt(value, 10)
      try {
        this.step.reparent_selected_bone(Number.isNaN(new_parent as number) ? null : new_parent)
        this.set_validation('')
      } catch (err) {
        this.set_validation((err as Error).message)
      }
    })

    const reference_input = this.q<HTMLInputElement>('#build-load-reference-input')
    this.q<HTMLButtonElement>('#build-load-reference-btn').addEventListener('click', () => {
      reference_input.value = ''
      reference_input.click()
    })
    reference_input.addEventListener('change', () => {
      const file = reference_input.files?.[0]
      if (file === undefined) return
      void this.handle_load_reference_glb(file)
    })
    this.q<HTMLButtonElement>('#build-clear-reference-btn').addEventListener('click', () => {
      this.step.clear_reference_object()
      this.set_reference_status('')
      this.render()
    })

    this.q<HTMLInputElement>('#build-selected-name-input').addEventListener('change', (e) => {
      const new_name = (e.target as HTMLInputElement).value
      try {
        this.step.rename_selected_bone(new_name)
        this.set_validation('')
      } catch (err) {
        this.set_validation((err as Error).message)
      }
    })

    this.root.querySelectorAll('[data-edit-mode]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const mode = (btn as HTMLElement).getAttribute('data-edit-mode') as BuildEditMode
        this.step.set_edit_mode(mode)
      })
    })

    this.q<HTMLButtonElement>('#build-delete-bone-btn').addEventListener('click', () => {
      this.step.delete_selected_bone()
    })

    this.q<HTMLInputElement>('#build-ik-chain-input').addEventListener('change', (e) => {
      const value = parseInt((e.target as HTMLInputElement).value, 10)
      if (!Number.isFinite(value) || value < 0) return
      this.step.set_selected_bone_ik_chain_length(value)
    })

    this.q<HTMLSelectElement>('#build-display-shape').addEventListener('change', (e) => {
      const value = (e.target as HTMLSelectElement).value as BoneDisplayShape
      // 'sphere' is the implicit default — clear the field so the descriptor
      // stays clean.
      this.step.set_selected_bone_display_shape(value === 'sphere' ? null : value)
    })

    const pole_handler = (): void => {
      const x = parseFloat(this.q<HTMLInputElement>('#build-pole-x').value)
      const y = parseFloat(this.q<HTMLInputElement>('#build-pole-y').value)
      const z = parseFloat(this.q<HTMLInputElement>('#build-pole-z').value)
      if (![x, y, z].every(Number.isFinite)) return
      if (x === 0 && y === 0 && z === 0) {
        this.step.set_selected_bone_pole_vector(null)
      } else {
        this.step.set_selected_bone_pole_vector([x, y, z])
      }
    }
    this.q<HTMLInputElement>('#build-pole-x').addEventListener('change', pole_handler)
    this.q<HTMLInputElement>('#build-pole-y').addEventListener('change', pole_handler)
    this.q<HTMLInputElement>('#build-pole-z').addEventListener('change', pole_handler)
    this.q<HTMLButtonElement>('#build-pole-clear').addEventListener('click', () => {
      this.step.set_selected_bone_pole_vector(null)
    })

    const limit_enable = this.q<HTMLInputElement>('#build-limits-enable')
    const limit_twist_axis = this.q<HTMLSelectElement>('#build-limit-twist-axis')
    const limit_twist_min = this.q<HTMLInputElement>('#build-limit-twist-min')
    const limit_twist_max = this.q<HTMLInputElement>('#build-limit-twist-max')
    const limit_swing_max = this.q<HTMLInputElement>('#build-limit-swing-max')
    const limits_handler = (): void => {
      if (!limit_enable.checked) {
        this.step.set_selected_bone_rotation_limits(null)
        this.set_validation('')
        return
      }
      const twist_min = parseFloat(limit_twist_min.value)
      const twist_max = parseFloat(limit_twist_max.value)
      const swing_max = parseFloat(limit_swing_max.value)
      if (!Number.isFinite(twist_min) || !Number.isFinite(twist_max) || !Number.isFinite(swing_max)) return
      if (twist_min > twist_max) {
        this.set_validation('Twist range: min cannot exceed max.')
        return
      }
      if (swing_max < 0) {
        this.set_validation('Swing cone must be non-negative.')
        return
      }
      this.set_validation('')
      this.step.set_selected_bone_rotation_limits({
        twist_axis: limit_twist_axis.value as 'x' | 'y' | 'z',
        twist: { min_deg: twist_min, max_deg: twist_max },
        swing: { max_deg: swing_max }
      })
    }
    limit_enable.addEventListener('change', limits_handler)
    limit_twist_axis.addEventListener('change', limits_handler)
    limit_twist_min.addEventListener('change', limits_handler)
    limit_twist_max.addEventListener('change', limits_handler)
    limit_swing_max.addEventListener('change', limits_handler)

    this.q<HTMLButtonElement>('#build-delete-subtree-btn').addEventListener('click', () => {
      const selected = this.step.get_selected_bone_index()
      if (selected === null) return
      const bones = this.step.get_bones()
      const has_descendants = bones.some(b => b.parent_index === selected)
      if (!has_descendants) {
        this.step.delete_selected_bone_with_descendants()
        return
      }
      void confirm_action({
        title: 'Delete bone subtree',
        message: `Delete "${bones[selected].name}" and all descendant bones?`,
        confirm_label: 'Delete',
        danger: true
      }).then((confirmed) => {
        if (confirmed) this.step.delete_selected_bone_with_descendants()
      })
    })

    this.q<HTMLButtonElement>('#build-mirror-bone-btn').addEventListener('click', () => {
      const result = this.step.mirror_selected_bone()
      if (!result.ok) {
        this.set_validation(result.message)
      } else {
        this.set_validation('')
      }
    })

    this.q<HTMLButtonElement>('#build-undo-btn').addEventListener('click', () => this.step.undo())
    this.q<HTMLButtonElement>('#build-redo-btn').addEventListener('click', () => this.step.redo())

    this.q<HTMLButtonElement>('#build-cancel-btn').addEventListener('click', () => this.on_cancel())
    this.q<HTMLButtonElement>('#build-save-btn').addEventListener('click', () => { void this.handle_save() })
  }

  private render (): void {
    const bones = this.step.get_bones()
    const selected = this.step.get_selected_bone_index()
    const add_mode = this.step.get_add_bone_mode()
    const edit_mode = this.step.get_edit_mode()
    const skeleton_name = this.step.get_skeleton_name()

    const name_input = this.q<HTMLInputElement>('#build-skeleton-name-input')
    if (document.activeElement !== name_input) name_input.value = skeleton_name

    this.q<HTMLButtonElement>('#build-add-child-btn').disabled = selected === null
    this.q<HTMLButtonElement>('#build-add-connected-child-btn').disabled = selected === null

    // Toggles reflect step state (so re-renders after re-mount stay in sync).
    this.q<HTMLInputElement>('#build-axis-triads-toggle').checked = this.step.are_axis_triads_visible()
    this.q<HTMLInputElement>('#build-influence-preview-toggle').checked = this.step.is_influence_preview_enabled()
    this.q<HTMLInputElement>('#build-snap-vertex-toggle').checked = this.step.is_snap_to_vertex_enabled()
    this.q<HTMLInputElement>('#build-live-mirror-toggle').checked = this.step.is_live_mirror_enabled()
    const hint = add_mode === 'place_root'
      ? 'Click in the viewport to place the root bone.'
      : add_mode === 'place_child'
        ? `Click in the viewport to place a child of "${bones[selected ?? 0]?.name ?? ''}".`
        : ''
    this.q<HTMLDivElement>('#build-add-mode-hint').textContent = hint

    // Bone list (flat with depth indent).
    const list = this.q<HTMLUListElement>('#build-bone-list')
    list.innerHTML = ''
    const depths = compute_depths(bones)
    bones.forEach((bone, i) => {
      const li = document.createElement('li')
      li.style.display = 'flex'
      li.style.alignItems = 'center'
      li.style.justifyContent = 'space-between'
      li.style.padding = '2px 4px'
      li.style.paddingLeft = `${4 + depths[i] * 12}px`
      if (i === selected) li.style.background = 'rgba(78, 125, 88, 0.25)'

      const name_span = document.createElement('span')
      name_span.textContent = bone.name
      name_span.style.flex = '1'
      name_span.style.cursor = 'pointer'
      if (bone.locked === true) {
        name_span.style.opacity = '0.7'
        name_span.style.fontStyle = 'italic'
      }
      name_span.addEventListener('click', () => this.step.select_bone(i))
      li.appendChild(name_span)

      const lock_btn = document.createElement('button')
      lock_btn.className = 'no-style-button'
      lock_btn.title = bone.locked === true ? 'Unlock bone' : 'Lock bone'
      lock_btn.style.padding = '0 4px'
      lock_btn.style.cursor = 'pointer'
      lock_btn.textContent = bone.locked === true ? '🔒' : '🔓'
      lock_btn.addEventListener('click', (e) => {
        e.stopPropagation()
        this.step.toggle_bone_lock(i)
      })
      li.appendChild(lock_btn)

      list.appendChild(li)
    })

    // Selected bone editor.
    const name_field = this.q<HTMLInputElement>('#build-selected-name-input')
    const delete_btn = this.q<HTMLButtonElement>('#build-delete-bone-btn')
    const delete_subtree_btn = this.q<HTMLButtonElement>('#build-delete-subtree-btn')
    const mirror_btn = this.q<HTMLButtonElement>('#build-mirror-bone-btn')
    const is_selected_locked = selected !== null && bones[selected].locked === true
    const ik_input = this.q<HTMLInputElement>('#build-ik-chain-input')
    const parent_select = this.q<HTMLSelectElement>('#build-selected-parent-select')
    if (selected !== null) {
      name_field.disabled = is_selected_locked
      if (document.activeElement !== name_field) name_field.value = bones[selected].name
      delete_btn.disabled = is_selected_locked
      delete_subtree_btn.disabled = is_selected_locked
      // Mirror is enabled whenever any bone is selected — even locked ones,
      // since mirroring doesn't modify the source bone.
      mirror_btn.disabled = false
      ik_input.disabled = is_selected_locked
      if (document.activeElement !== ik_input) ik_input.value = (bones[selected].ik_chain_length ?? 0).toString()
      const shape_select = this.q<HTMLSelectElement>('#build-display-shape')
      shape_select.disabled = is_selected_locked
      if (document.activeElement !== shape_select) shape_select.value = bones[selected].display_shape ?? 'sphere'
      this.update_pole_and_limits_inputs(bones[selected], is_selected_locked)
      this.root.querySelectorAll<HTMLButtonElement>('[data-edit-mode]').forEach((btn) => {
        btn.disabled = is_selected_locked
        const is_active = btn.getAttribute('data-edit-mode') === edit_mode
        btn.style.fontWeight = is_active ? '700' : 'normal'
      })

      // Populate parent select with all valid parents (excluding self and any
      // descendants — those would create a cycle that BoneTreeEditor.reparent
      // rejects). Filtering here gives a tidier dropdown than relying on the
      // catch path.
      parent_select.disabled = is_selected_locked
      parent_select.innerHTML = ''
      const root_opt = document.createElement('option')
      root_opt.value = ''
      root_opt.textContent = '(none — root bone)'
      parent_select.appendChild(root_opt)
      const descendants = compute_descendant_indices(bones, selected)
      bones.forEach((b, i) => {
        if (i === selected || descendants.has(i)) return
        const opt = document.createElement('option')
        opt.value = i.toString()
        opt.textContent = b.name
        parent_select.appendChild(opt)
      })
      const current_parent = bones[selected].parent_index
      parent_select.value = current_parent === null ? '' : current_parent.toString()
    } else {
      name_field.disabled = true
      name_field.value = ''
      delete_btn.disabled = true
      delete_subtree_btn.disabled = true
      mirror_btn.disabled = true
      ik_input.disabled = true
      ik_input.value = '0'
      const shape_select = this.q<HTMLSelectElement>('#build-display-shape')
      shape_select.disabled = true
      shape_select.value = 'sphere'
      this.update_pole_and_limits_inputs(null, true)
      this.root.querySelectorAll<HTMLButtonElement>('[data-edit-mode]').forEach((btn) => {
        btn.disabled = true
        btn.style.fontWeight = 'normal'
      })
      parent_select.disabled = true
      parent_select.innerHTML = ''
    }

    this.q<HTMLButtonElement>('#build-undo-btn').disabled = !this.step.can_undo()
    this.q<HTMLButtonElement>('#build-redo-btn').disabled = !this.step.can_redo()
    this.q<HTMLButtonElement>('#build-clear-reference-btn').disabled = !this.step.has_reference_object()
  }

  private async handle_load_reference_glb (file: File): Promise<void> {
    const existing_bone_count = this.step.get_bones().length
    if (existing_bone_count > 0) {
      const proceed = await confirm_action({
        title: 'Load reference GLB',
        message: `Loading the reference GLB will replace the ${existing_bone_count} bone(s) currently in the editor with bones from the GLB (use Undo to revert). Continue?`,
        confirm_label: 'Load reference'
      })
      if (!proceed) return
    }
    const url = URL.createObjectURL(file)
    this.set_reference_status(`Loading "${file.name}"…`)
    try {
      const result = await this.step.load_reference_glb_from_url(url)
      const parts: string[] = [`Loaded "${file.name}"`]
      if (result.bones_imported > 0) parts.push(`imported ${result.bones_imported} bone(s)`)
      else parts.push('no skeleton found — using as visual reference only')
      if (result.has_mesh) parts.push('mesh available for snap-to-surface placement')
      this.set_reference_status(parts.join(' — '))
      this.render()
    } catch (err) {
      this.set_reference_status(`Failed to load "${file.name}": ${(err as Error).message}`)
    } finally {
      URL.revokeObjectURL(url)
    }
  }

  private set_reference_status (message: string): void {
    const el = this.root.querySelector<HTMLDivElement>('#build-reference-status')
    if (el !== null) el.textContent = message
  }

  private set_tools_status (message: string): void {
    const el = this.root.querySelector<HTMLDivElement>('#build-tools-status')
    if (el !== null) el.textContent = message
  }

  private async handle_save (): Promise<void> {
    const name = this.step.get_skeleton_name().trim()
    const name_error = validate_skeleton_name(name)
    if (name_error !== null) {
      this.set_validation(name_error)
      return
    }
    const validation = validate_bones([...this.step.get_bones()])
    if (!validation.ok) {
      this.set_validation(validation.errors.join(' '))
      return
    }
    const descriptor = this.step.get_descriptor()
    if (this.capture_thumbnail !== undefined) {
      const thumb = this.capture_thumbnail(192, 144)
      if (thumb !== undefined) descriptor.thumbnail_data_url = thumb
    }
    const saved = await CustomSkeletonStorage.put(descriptor)
    if (!saved) {
      this.set_validation('Could not save skeleton. Your browser storage may be unavailable or blocked.')
      return
    }
    this.set_validation('')
    this.on_save(descriptor)
  }

  private set_validation (message: string): void {
    this.q<HTMLDivElement>('#build-validation-message').textContent = message
  }

  private update_pole_and_limits_inputs (
    bone: {
      pole_vector?: [number, number, number]
      rotation_limits?: {
        twist_axis?: 'x' | 'y' | 'z'
        twist?: { min_deg: number, max_deg: number }
        swing?: { max_deg: number }
      }
    } | null,
    disabled: boolean
  ): void {
    const pole = bone?.pole_vector
    const px = this.q<HTMLInputElement>('#build-pole-x')
    const py = this.q<HTMLInputElement>('#build-pole-y')
    const pz = this.q<HTMLInputElement>('#build-pole-z')
    const pclear = this.q<HTMLButtonElement>('#build-pole-clear')
    px.disabled = py.disabled = pz.disabled = pclear.disabled = disabled || bone === null
    if (document.activeElement !== px) px.value = (pole?.[0] ?? 0).toString()
    if (document.activeElement !== py) py.value = (pole?.[1] ?? 0).toString()
    if (document.activeElement !== pz) pz.value = (pole?.[2] ?? 0).toString()

    const limits = bone?.rotation_limits
    const has_limits = limits !== undefined
    const enable = this.q<HTMLInputElement>('#build-limits-enable')
    const twist_axis = this.q<HTMLSelectElement>('#build-limit-twist-axis')
    const twist_min = this.q<HTMLInputElement>('#build-limit-twist-min')
    const twist_max = this.q<HTMLInputElement>('#build-limit-twist-max')
    const swing_max = this.q<HTMLInputElement>('#build-limit-swing-max')
    enable.disabled = disabled || bone === null
    enable.checked = has_limits
    const inputs_disabled = enable.disabled || !has_limits
    twist_axis.disabled = inputs_disabled
    twist_min.disabled = inputs_disabled
    twist_max.disabled = inputs_disabled
    swing_max.disabled = inputs_disabled
    if (document.activeElement !== twist_axis) twist_axis.value = limits?.twist_axis ?? 'y'
    if (document.activeElement !== twist_min) twist_min.value = (limits?.twist?.min_deg ?? -180).toString()
    if (document.activeElement !== twist_max) twist_max.value = (limits?.twist?.max_deg ?? 180).toString()
    if (document.activeElement !== swing_max) swing_max.value = (limits?.swing?.max_deg ?? 180).toString()
  }

  private q <T extends HTMLElement> (selector: string): T {
    const el = this.root.querySelector(selector) as T | null
    if (el === null) throw new Error(`BuildSkeletonPanel: missing element ${selector}`)
    return el
  }
}

function compute_descendant_indices (
  bones: readonly { parent_index: number | null }[],
  root_index: number
): Set<number> {
  const out = new Set<number>()
  const stack: number[] = [root_index]
  while (stack.length > 0) {
    const current = stack.pop() as number
    for (let i = 0; i < bones.length; i++) {
      if (bones[i].parent_index === current && !out.has(i) && i !== root_index) {
        out.add(i)
        stack.push(i)
      }
    }
  }
  return out
}

function compute_depths (bones: readonly { parent_index: number | null }[]): number[] {
  const depths: number[] = new Array(bones.length).fill(0)
  for (let i = 0; i < bones.length; i++) {
    let cursor = bones[i].parent_index
    let depth = 0
    while (cursor !== null && depth < bones.length) {
      depth++
      cursor = bones[cursor]?.parent_index ?? null
    }
    depths[i] = depth
  }
  return depths
}
